<!--赖传峰-->
<!--2022/2/9-->
<template>
  <ClientOnly>
    <ElDrawer
      v-model="layoutStore.sidebarMobileVisible"
      :size="290"
      :with-header="false"
      class="sidebar"
      direction="ltr"
    >
      <template v-if="userInfoStore.userId">
        <div class="top-part">
          <UserInfoPopover ref="useInfoPopover" />
          <DiskUsage />
        </div>
        <div class="bottom-part">
          <SidebarNavLinks />
          <ElDivider />
          <NuxtLinkLocale
            class="pp-link-button"
            :to="RoutePath.HELP_CENTER"
            target="_blank"
          >
            {{ $t('help-center') }}
          </NuxtLinkLocale>
          <HeaderBarRedeem />
          <I18nMenuMobile />
          <ElDivider />
          <a
            :href="RoutePathOfDrive.ACCOUNT"
            class="pp-link-button"
          >
            {{ $t('account-page-title') }}
          </a>
          <a
            class="pp-link-button"
            @click="logout"
          >
            {{ $t('logout') }}
          </a>
        </div>
      </template>
      <template v-else>
        <div class="not-login">
          <a @click="() => showLoginDialog?.()">
            {{ $t('login') }}
            &nbsp;|&nbsp;
            {{ $t('register') }}
          </a>
        </div>
        <ElDivider />
        <div class="introduction">
          <div class="introduction-header">
            <div>
              {{ $t('pikpak-introduce') }}
            </div>
            <NuxtLink to="/">
              {{ $t('learn-more') }}
              <DownIcon
                color="var(--color-primary)"
                style="transform: rotate(-90deg) scale(0.8);"
              />
            </NuxtLink>
          </div>
          <ul class="features">
            <li
              v-for="item in features"
              :key="item.imgSrc"
            >
              <figure>
                <div class="illustration">
                  <img
                    :alt="$t(item.caption).replace('<br>', ';')"
                    :src="item.imgSrc"
                  >
                </div>
                <figcaption>{{ $t(item.caption) }}</figcaption>
              </figure>
            </li>
          </ul>
        </div>
        <ElDivider />
        <div class="i18n">
          <I18nMenuMobile />
        </div>
      </template>
      <ElDivider />
      <div
        class="install-guide"
      >
        <div class="title">
          {{ $t('install-pikpak-app') }}
        </div>
        <ul class="install-channels">
          <li
            v-for="item in installChannels.slice(0, isIOS ? 1 : 2).filter(i => !i.hide)"
            :key="item.imgSrc.value"
            @click="handleChaClick(item)"
          >
            <a
              :href="item.href"
              :style="{ 'background-image': `url(${item.imgSrc.value || ''})` }"
            />
          </li>
        </ul>
      </div>
    </ElDrawer>
  </ClientOnly>
</template>

<script
  lang="ts"
  setup
>
import {
  inject,
  ref,
  useRequestHeaders,
} from '#imports'
import DownIcon from '@/components/icons/DownIcon.vue'
import useInstallChannels from '@/composables/useInstallChannels'
import { features } from '@/config'
import { useLayout } from '@/store/layout'
import { useUserInfo } from '@/store/userInfo'
import {
  InjectionKey,
  OSName,
  RoutePath,
  RoutePathOfDrive,
} from '@/types/enum'
import uaParser from 'ua-parser-js'
import DiskUsage from './DiskUsage.vue'
import HeaderBarRedeem from './HeaderBarRedeem.vue'
import I18nMenuMobile from './I18nMenu.m.vue'
import SidebarNavLinks from './SidebarNavLinks.vue'
import UserInfoPopover from './UserInfoPopover.vue'

const useInfoPopover = ref<InstanceType<typeof UserInfoPopover>>()
const layoutStore = useLayout()
const userInfoStore = useUserInfo()
const isIOS = (uaParser(useRequestHeaders(['user-agent'])['user-agent']).os.name === OSName.IOS)

const installChannels = useInstallChannels('websidebar')

const logout = () => useInfoPopover.value?.logout()

const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)

const handleChaClick = (channel: typeof installChannels.value[0]) => {
  if (channel.trackEventButtonParameter !== 'google') {
    navigator.clipboard.writeText(window.location.href || '')
  }
}
</script>

<style
  lang="less"
  scoped
>

.top-part {
  padding: 16px;
  height: 140px;
  border-bottom: 1px solid #e3e7eb;
}

.bottom-part {
  padding: 0 12px;
  overflow: auto;
  height: calc(var(--vh, 100vh) - 140px - 123px);
}

.el-divider--horizontal {
  margin: 8px 0;
}

.pp-link-button {
  justify-content: flex-start;
  margin-left: 14px;
  height: 44px;
}

.install-guide {
  display: flex;
  flex-direction: column;
  height: 106px;
  padding: 16px 24px 24px;
  justify-content: center;

  .title {
    padding-left: 24px;
    line-height: 18px;
    font-size: 13px;
    background: url('../../public/favicon-32x32.png') no-repeat left/16px;
  }

  .install-channels {
    display: flex;
    justify-content: space-between;
    margin: 12px -4px 0;

    a {
      display: block;
      width: 120px;
      height: 34px;
      background-size: 100%;
      background-position: center;
    }
  }
}

.not-login {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  height: 92px;
  padding: 0 12px 0 76px;
  background: url('@/assets/images/drive-layout/not-login-avatar.png') no-repeat 16px/48px;
}

.introduction {
  display: flex;
  padding: 0 24px;
  height: 160px;
  flex-direction: column;
  justify-content: center;

  .introduction-header {
    line-height: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    a {
      display: flex;
      align-items: center;
      text-decoration: none;
    }
  }

  .features {
    margin-top: 24px;
    display: flex;
    width: 100%;
    justify-content: space-between;
    text-align: center;

    li {
      flex: 0 0 30%;
    }

    .illustration img {
      height: 48px;
    }

    figcaption {
      font-weight: 400;
      font-size: 10px;
      line-height: 14px;
      margin-top: 8px;
    }
  }
}

.i18n {
  height: calc(var(--vh, 100vh) - 409px);
  overflow: auto;
}
</style>
<style lang="less">
@import '@/assets/styles/util.less';

@media screen and (max-width: @mobile-break-width) {
  .sidebar {
    .el-drawer__body {
      --el-drawer-padding-primary: 0;
    }
  }
}
</style>
